// src/components/shared/DashboardLayout.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationAPI, grievanceAPI } from '../../services/api';
import { getMediaUrl } from '../../utils/mediaUrl';
import '../../styles/Dashboard.css';
import '../../styles/Grievance.css';

// ─── Inline Submit Complaint Modal ────────────────────────────────────────────
const CATEGORIES = [
  { value: 'ACADEMIC',       label: 'Academic' },
  { value: 'BILLING',        label: 'Billing' },
  { value: 'LAB',            label: 'Laboratory' },
  { value: 'HOSTEL',         label: 'Hostel' },
  { value: 'LIBRARY',        label: 'Library' },
  { value: 'TRANSPORT',      label: 'Transport' },
  { value: 'RECEPTION',      label: 'Reception / OPD' },
  { value: 'REPORT',         label: 'Report Generation' },
  { value: 'ADMINISTRATIVE', label: 'Administrative' },
  { value: 'OTHER',          label: 'Other' },
];

const PRIORITIES = [
  { value: 'LOW',    label: 'Low',    color: '#10b981' },
  { value: 'MEDIUM', label: 'Medium', color: '#f59e0b' },
  { value: 'HIGH',   label: 'High',   color: '#ef4444' },
];

const EMPTY_FORM = { category: '', subject: '', description: '', priority: 'MEDIUM', attachment: null };

const SubmitComplaintModal = ({ open, onClose }) => {
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successId,  setSuccessId]  = useState(null);
  const fileRef = useRef(null);

  // Reset whenever the modal opens
  useEffect(() => {
    if (open) { setForm(EMPTY_FORM); setErrors({}); setSuccessId(null); }
  }, [open]);

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.category)          e.category    = 'Required';
    if (!form.subject.trim())    e.subject     = 'Required';
    if (!form.description.trim()) e.description = 'Required';
    return e;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm(prev => ({ ...prev, [name]: files ? files[0] : value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('category',    form.category);
      fd.append('subject',     form.subject);
      fd.append('description', form.description);
      fd.append('priority',    form.priority);
      if (form.attachment) fd.append('attachment', form.attachment);

      const result = await grievanceAPI.submit(fd);
      setSuccessId(result.grievance_id);
    } catch (err) {
      setErrors({ _global: err.response?.data?.detail || 'Failed to submit. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540,
          maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.25rem 1.5rem', borderBottom: '1px solid #f3f4f6',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#d97706" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Submit a Complaint</h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>We'll route it to the right department</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9ca3af', borderRadius: 6, padding: '0.25rem',
            }}
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {/* Success state */}
          {successId ? (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <div style={{
                width: 60, height: 60, background: '#d1fae5', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
              }}>
                <svg width="30" height="30" fill="none" viewBox="0 0 24 24" stroke="#059669" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 style={{ color: '#065f46', margin: '0 0 0.5rem', fontSize: '1.1rem' }}>Complaint Submitted!</h4>
              <p style={{ color: '#6b7280', margin: '0 0 1rem', fontSize: '0.875rem' }}>
                Your complaint has been recorded.
              </p>
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
                padding: '0.5rem 1rem', display: 'inline-block', marginBottom: '1.5rem',
              }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Reference ID </span>
                <span style={{
                  fontFamily: 'monospace', fontWeight: 700, color: '#6366f1',
                  background: '#eef2ff', padding: '2px 8px', borderRadius: 4, fontSize: '0.875rem',
                }}>
                  {successId}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <button
                  onClick={onClose}
                  style={{
                    padding: '0.5rem 1.25rem', background: '#6366f1', color: '#fff',
                    border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 500, fontSize: '0.875rem',
                  }}
                >
                  Done
                </button>
                <button
                  onClick={() => { setSuccessId(null); setForm(EMPTY_FORM); }}
                  style={{
                    padding: '0.5rem 1.25rem', background: '#fff', color: '#6366f1',
                    border: '1px solid #6366f1', borderRadius: 8, cursor: 'pointer', fontWeight: 500, fontSize: '0.875rem',
                  }}
                >
                  Submit Another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {errors._global && (
                <div style={{
                  background: '#fee2e2', color: '#991b1b', padding: '0.625rem 0.875rem',
                  borderRadius: 8, marginBottom: '1rem', fontSize: '0.8rem',
                }}>
                  {errors._global}
                </div>
              )}

              {/* Category */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Category <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  name="category" value={form.category} onChange={handleChange}
                  style={{
                    width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, fontSize: '0.875rem',
                    border: `1px solid ${errors.category ? '#ef4444' : '#d1d5db'}`,
                    background: '#fff', color: '#111827', boxSizing: 'border-box',
                  }}
                >
                  <option value="">Select category…</option>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                {errors.category && <p style={{ color: '#ef4444', fontSize: '0.7rem', margin: '0.2rem 0 0' }}>{errors.category}</p>}
              </div>

              {/* Priority */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Priority
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {PRIORITIES.map(p => (
                    <button
                      key={p.value} type="button"
                      onClick={() => setForm(prev => ({ ...prev, priority: p.value }))}
                      style={{
                        flex: 1, padding: '0.4rem 0',
                        border: `2px solid ${form.priority === p.value ? p.color : '#e5e7eb'}`,
                        borderRadius: 8,
                        background: form.priority === p.value ? `${p.color}18` : '#fff',
                        color: form.priority === p.value ? p.color : '#6b7280',
                        cursor: 'pointer', fontWeight: form.priority === p.value ? 700 : 400,
                        fontSize: '0.8rem', transition: 'all 0.15s',
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Subject <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text" name="subject" value={form.subject} onChange={handleChange}
                  placeholder="Brief title of your complaint"
                  style={{
                    width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, fontSize: '0.875rem',
                    border: `1px solid ${errors.subject ? '#ef4444' : '#d1d5db'}`,
                    boxSizing: 'border-box',
                  }}
                />
                {errors.subject && <p style={{ color: '#ef4444', fontSize: '0.7rem', margin: '0.2rem 0 0' }}>{errors.subject}</p>}
              </div>

              {/* Description */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Description <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  name="description" value={form.description} onChange={handleChange}
                  rows={4} placeholder="Describe the issue in detail…"
                  style={{
                    width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, fontSize: '0.875rem',
                    border: `1px solid ${errors.description ? '#ef4444' : '#d1d5db'}`,
                    resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                  }}
                />
                {errors.description && <p style={{ color: '#ef4444', fontSize: '0.7rem', margin: '0.2rem 0 0' }}>{errors.description}</p>}
              </div>

              {/* Attachment */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Attachment <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                </label>
                <input
                  ref={fileRef} type="file" name="attachment" onChange={handleChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ fontSize: '0.8rem', color: '#374151' }}
                />
                {form.attachment && (
                  <p style={{ fontSize: '0.75rem', color: '#6366f1', margin: '0.2rem 0 0' }}>
                    ✓ {form.attachment.name}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="submit" disabled={submitting}
                  style={{
                    flex: 1, padding: '0.625rem', background: submitting ? '#a5b4fc' : '#6366f1',
                    color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.875rem',
                    fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
                  }}
                >
                  {submitting ? 'Submitting…' : 'Submit Complaint'}
                </button>
                <button
                  type="button" onClick={onClose}
                  style={{
                    padding: '0.625rem 1.25rem', background: '#fff', color: '#374151',
                    border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DashboardLayout
// ─────────────────────────────────────────────────────────────────────────────
const DashboardLayout = ({ children, title, fullWidth = false }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useAuth();

  const [showProfileMenu,    setShowProfileMenu]    = useState(false);
  const [showNotifications,  setShowNotifications]  = useState(false);
  const [sidebarOpen,        setSidebarOpen]        = useState(true);
  const [notifications,      setNotifications]      = useState([]);
  const [unreadCount,        setUnreadCount]        = useState(0);
  const [showComplaintModal, setShowComplaintModal] = useState(false);

  const notificationRef = useRef(null);
  const profileRef      = useRef(null);

  const isProfessorHOD = user?.role === 'FACULTY' && user?.profile?.faculty_subrole === 'PROFESSOR_HOD';
  const isHODView      = location.pathname === '/hod-dashboard';
  const isFacultyView  = location.pathname === '/faculty-dashboard';

  // Hide complaint button only for GRIEVANCE_OFFICER role (they manage, not submit)
  const showComplaintButton = user?.role !== 'GRIEVANCE_OFFICER';

  // Dept staff roles — shown the Department Grievances shortcut in the profile menu
  const DEPT_STAFF_ROLES = [
  'ADMIN','PRINCIPAL', 'DEAN',
  'BILLING_MANAGER', 'REPORT_GENERATION', 'SAMPLE_COLLECTOR',
  'LAB_TECH_MICROBIOLOGY', 'LAB_TECH_PATHOLOGY', 'LAB_TECH_BIOCHEMISTRY',
  'recepsonist', 'HOSTEL_ADMIN', 'LIBRARY_STAFF', 'TRANSPORT_MANAGER',
  'FACULTY', 'FINANCE',
];
  const isDeptStaff = DEPT_STAFF_ROLES.includes(user?.role);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await notificationAPI.getNotifications();
      const list = Array.isArray(data) ? data : (data.results || []);
      setNotifications(list);
    } catch { setNotifications([]); }
  };

  const loadUnreadCount = async () => {
    try {
      const data = await notificationAPI.getUnreadCount();
      setUnreadCount(data.count || 0);
    } catch { setUnreadCount(0); }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        await notificationAPI.markAsRead(notification.id);
        await loadNotifications();
        await loadUnreadCount();
      }
      if (notification.link) navigate(notification.link);
      setShowNotifications(false);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSwitchView = () => {
    navigate(isHODView ? '/faculty-dashboard' : '/hod-dashboard');
  };

  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.username?.substring(0, 2).toUpperCase() || 'U';
  };

  const getDisplayRole = () => {
    if (user?.role === 'FACULTY' && user?.profile?.faculty_subrole) {
      const subroleMap = {
        'SENIOR_RESIDENT':      'Senior Resident',
        'ASSISTANT_PROFESSOR':  'Assistant Professor',
        'ASSOCIATE_PROFESSOR':  'Associate Professor',
        'PROFESSOR':            'Professor',
        'HEAD_OF_DEPARTMENT':   'Head of Department',
        'PROFESSOR_HOD':        'Professor + HOD',
        'DEAN':                 'Dean',
      };
      return subroleMap[user.profile.faculty_subrole] || user.role;
    }
    return user?.role;
  };

  const getNotificationIcon = (type) => {
    const iconProps = {
      width: '24', height: '24', viewBox: '0 0 24 24',
      fill: 'none', stroke: 'currentColor',
      strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round',
    };
    switch (type) {
      case 'NEW_USER':
        return <svg {...iconProps}><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
      case 'ADMISSION_APPROVED':
        return <svg {...iconProps}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'ADMISSION_REJECTED':
        return <svg {...iconProps}><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>;
      default:
        return <svg {...iconProps}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>;
    }
  };

  const getNotificationIconColor = (type) => {
    const map = { NEW_USER: '#3b82f6', ADMISSION_APPROVED: '#10b981', ADMISSION_REJECTED: '#ef4444' };
    return map[type] || '#6b7280';
  };

  const formatTimeAgo = (dateString) => {
    const now  = new Date();
    const date = new Date(dateString);
    const secs = Math.floor((now - date) / 1000);
    if (secs < 60)     return 'Just now';
    if (secs < 3600)   return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400)  return `${Math.floor(secs / 3600)}h ago`;
    if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      NEW_USER: 'New Registration', ADMISSION_APPROVED: 'Approved',
      ADMISSION_REJECTED: 'Rejected', SYSTEM: 'System',
      INFO: 'Information', WARNING: 'Warning',
    };
    return labels[type] || 'Notification';
  };

  return (
    <div className="dashboard-layout">
      {/* ── Top Navbar ──────────────────────────────────────────────────────── */}
      <nav className="dashboard-navbar">
        <div className="navbar-left">
          <div className="brand">
            <svg width="32" height="32" viewBox="0 0 50 50" fill="none">
              <path d="M25 5L45 15V35L25 45L5 35V15L25 5Z" fill="#4F46E5" stroke="#4F46E5" strokeWidth="2" />
              <path d="M25 15V35M15 20H35M20 25H30" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            <span className="brand-text">Medical ERP</span>
          </div>
        </div>

        <div className="navbar-right">

          {/* ── Role Switcher (Professor+HOD) ──────────────────────────────── */}
          {isProfessorHOD && (isFacultyView || isHODView) && (
            <div className="role-switcher">
              <button className="role-switch-button" onClick={handleSwitchView}
                title={isHODView ? 'Switch to Faculty View' : 'Switch to HOD View'}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                <span className="role-switch-text">
                  {isHODView ? 'Switch to Faculty View' : 'Switch to HOD View'}
                </span>
              </button>
            </div>
          )}

          {/* ── Submit Complaint Button ─────────────────────────────────────── */}
          {showComplaintButton && (
            <button
              onClick={() => {
                setShowComplaintModal(true);
                setShowNotifications(false);
                setShowProfileMenu(false);
              }}
              title="Submit a Complaint"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.375rem 0.875rem',
                background: '#fef3c7',
                color: '#92400e',
                border: '1px solid #fde68a',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#fde68a';
                e.currentTarget.style.borderColor = '#f59e0b';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#fef3c7';
                e.currentTarget.style.borderColor = '#fde68a';
              }}
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="complaint-btn-text">Complaint</span>
            </button>
          )}

          {/* ── Notifications ──────────────────────────────────────────────── */}
          <div className="notification-container" ref={notificationRef}>
            <button className="icon-button"
              onClick={() => { setShowNotifications(!showNotifications); setShowProfileMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>

            {showNotifications && (
              <div className="notification-dropdown">
                <div className="notification-header">
                  <h3>Notifications</h3>
                  {unreadCount > 0 && (
                    <button className="btn-link" onClick={handleMarkAllRead}>Mark all read</button>
                  )}
                </div>
                <div className="notification-list">
                  {!Array.isArray(notifications) || notifications.length === 0 ? (
                    <div className="notification-empty">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <p>No notifications yet</p>
                      <span>You're all caught up!</span>
                    </div>
                  ) : (
                    notifications.map(notification => (
                      <div key={notification.id}
                        className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(notification)}>
                        <div className="notification-icon-circle"
                          style={{ backgroundColor: `${getNotificationIconColor(notification.notification_type)}15` }}>
                          <div style={{ color: getNotificationIconColor(notification.notification_type) }}>
                            {getNotificationIcon(notification.notification_type)}
                          </div>
                        </div>
                        <div className="notification-content">
                          <div className="notification-header-row">
                            <span className="notification-type-label">
                              {getNotificationTypeLabel(notification.notification_type)}
                            </span>
                            <span className="notification-time">{formatTimeAgo(notification.created_at)}</span>
                          </div>
                          <h4 className="notification-title">{notification.title}</h4>
                          <p className="notification-message">{notification.message}</p>
                          {!notification.is_read && <div className="notification-unread-dot" />}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Profile Menu ────────────────────────────────────────────────── */}
          <div className="profile-menu-container" ref={profileRef}>
            <button className="profile-button"
              onClick={() => { setShowProfileMenu(!showProfileMenu); setShowNotifications(false); }}>
              {user?.profile_picture ? (
                <img src={getMediaUrl(user.profile_picture)} alt="Profile"
                  className="profile-avatar-img"
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <div className="profile-avatar"
                style={{ display: user?.profile_picture ? 'none' : 'flex' }}>
                {getInitials()}
              </div>
              <div className="profile-info">
                <span className="profile-name">{user?.first_name} {user?.last_name}</span>
                <span className="profile-role">{getDisplayRole()}</span>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showProfileMenu && (
              <div className="profile-dropdown">
                <button onClick={() => navigate('/profile')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  My Profile
                </button>
                <button onClick={() => navigate('/change-password')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Change Password
                </button>

                {/* My Complaints + Department Grievances shortcuts */}
                {showComplaintButton && (
                  <>
                    <div className="dropdown-divider" />
                    <button onClick={() => { setShowProfileMenu(false); navigate('/my-grievances'); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      My Complaints
                    </button>

                    {/* Department Grievances — only for staff who handle assigned grievances */}
                    {isDeptStaff && (
                      <button onClick={() => { setShowProfileMenu(false); navigate('/department-grievances'); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Department Grievances
                      </button>
                    )}
                  </>
                )}

                <div className="dropdown-divider" />
                <button onClick={handleLogout} className="logout-button">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── Main Content ─────────────────────────────────────────────────────── */}
      <div className="dashboard-main">
        <main className="dashboard-content" style={fullWidth ? { maxWidth: '100%', width: '100%', margin: 0 } : {}}>
          {(title || (isProfessorHOD && (isFacultyView || isHODView))) && (
            <div className="content-header">
              {title && <h1>{title}</h1>}
              {isProfessorHOD && (isFacultyView || isHODView) && (
                <div className="current-view-indicator">
                  <span className="view-badge">
                    {isHODView ? '🎓 HOD View' : '👨‍🏫 Faculty View'}
                  </span>
                </div>
              )}
            </div>
          )}
          {children}
        </main>
      </div>

      {/* ── Complaint Modal (portal-style, rendered at layout level) ─────────── */}
      <SubmitComplaintModal
        open={showComplaintModal}
        onClose={() => setShowComplaintModal(false)}
      />
    </div>
  );
};

export default DashboardLayout;